// Sprint 65.1 — Deal Baazar premium product card with LIVE Smart-OOS.
//
// • Reads live product fields directly from the product object.
// • When stock === 0 (or is_active === false), we show an OOS overlay AND
//   a NOTIFY ME button, keeping the product in its original grid slot.
// • When stock returns, the ADD button is automatically restored.

import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Package, Bolt, Bell } from "lucide-react";
import QtyStepper from "@/components/customer/QtyStepper";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

const money = (v) => `₹${Math.round(Number(v || 0))}`;

export default function DealProductCard({ product, campaign, testId }) {
  const p = product;
  const { user } = useAuth();
  const nav = useNavigate();
  const [notified, setNotified] = useState(false);
  if (!p) return null;

  const original = Number(p.mrp || p.selling_price || 0);
  const deal     = Number(p.selling_price || 0);
  const save     = Math.max(0, original - deal);
  const pct      = original > 0 ? Math.round((save / original) * 100) : 0;
  const theme    = campaign?.theme_color || "#DC1E1E";
  const outOfStock = p.stock === 0 || p.is_active === false;

  const onNotify = async (e) => {
    e.preventDefault(); e.stopPropagation();
    if (!user) { nav(`/login?redirect=${encodeURIComponent(window.location.pathname)}`); return; }
    if (notified) { toast.info("You'll be notified when it's back."); return; }
    try {
      await api.post(`/products/${p.id}/notify-me`).catch(() => null);
      setNotified(true);
      toast.success(`We'll notify you when ${p.name} is back in stock`);
    } catch (err) { toast.error(errMsg(err)); }
  };

  return (
    <div
      className="group relative bg-white border-2 rounded-2xl overflow-hidden flex flex-col h-full transition hover:-translate-y-0.5"
      style={{ borderColor: `${theme}22`, boxShadow: `0 4px 16px -6px ${theme}22` }}
      data-testid={testId || `deal-card-${p.id}`}
    >
      <Link
        to={`/product/${p.id}`}
        className="block relative bg-white"
        style={{ aspectRatio: "4/3", padding: 6 }}
      >
        <div className={`w-full h-full rounded-xl overflow-hidden bg-[hsl(0_60%_98%)] grid place-items-center ${outOfStock ? "opacity-40 grayscale" : ""}`}>
          {p.thumbnail_url
            ? <img src={p.thumbnail_url} alt={p.name} loading="lazy" className="w-full h-full object-contain group-hover:scale-[1.06] transition duration-500" />
            : <Package className="w-10 h-10 text-primary/30" />}
        </div>

        {pct > 0 && !outOfStock && (
          <div
            className="absolute top-1.5 left-1.5 rounded-md px-1.5 py-0.5 text-white font-black tracking-tight text-[11px] shadow"
            style={{ background: theme }}
            data-testid={`deal-pct-${p.id}`}
          >
            {pct}% OFF
          </div>
        )}
        {p._dealTag && !outOfStock && (
          <div className="absolute top-1.5 right-1.5 rounded-md px-1.5 py-0.5 bg-black/85 text-white text-[9px] font-bold uppercase tracking-widest">
            {String(p._dealTag).replace(/_/g, " ")}
          </div>
        )}
        {outOfStock && (
          <div className="absolute inset-0 grid place-items-center pointer-events-none" data-testid={`deal-oos-${p.id}`}>
            <div className="rounded-lg bg-foreground/90 text-background px-3 py-1.5 text-[11px] font-bold uppercase tracking-widest shadow">
              Out of stock
            </div>
          </div>
        )}
      </Link>

      <div className="flex flex-col flex-1 gap-1 px-2 pt-1.5 pb-2">
        <Link to={`/product/${p.id}`}>
          <h3
            className="font-semibold text-foreground/95"
            style={{
              fontSize: 13, lineHeight: 1.2, display: "-webkit-box",
              WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden",
              minHeight: 13 * 1.2 * 2,
            }}
          >{p.name}</h3>
        </Link>
        {p.weight && (
          <div className="text-[10px] text-muted-foreground truncate">
            {p.weight} {p.weight_unit || "g"}
          </div>
        )}

        <div className="flex items-end justify-between gap-1 mt-auto pt-0.5">
          <div className="min-w-0 flex-1 flex flex-col leading-[1.1]">
            <span className="tabular-nums font-black text-[17px]" style={{ color: theme }}>
              {money(deal)}
            </span>
            {save > 0 && (
              <span className="flex items-center gap-1 leading-none">
                <span className="tabular-nums line-through text-muted-foreground text-[11px]">{money(original)}</span>
                <span
                  className="inline-flex items-center gap-0.5 rounded px-1 py-[1px] text-white font-bold uppercase tracking-wide text-[9px]"
                  style={{ background: theme }}
                >
                  <Bolt className="w-2.5 h-2.5" /> Save ₹{save}
                </span>
              </span>
            )}
          </div>
          <div className="shrink-0">
            {outOfStock ? (
              <button
                onClick={onNotify}
                className={`inline-flex items-center justify-center gap-1 border-2 font-bold tracking-wide transition press ${notified ? "border-emerald-500 bg-emerald-50 text-emerald-700" : "border-primary/50 bg-primary/5 text-primary hover:bg-primary hover:text-primary-foreground"}`}
                style={{ width: 72, height: 30, borderRadius: 8, fontSize: 10 }}
                data-testid={`deal-notify-${p.id}`}
                title={notified ? "You'll be notified" : "Notify me when back in stock"}
              >
                <Bell className="w-3 h-3" />
                <span>{notified ? "SET" : "NOTIFY"}</span>
              </button>
            ) : (
              <QtyStepper
                product={p}
                size="sm"
                fullWidth
                testIdPrefix="deal-qty"
                addLabel="ADD"
                style={{
                  width: 68, height: 30, borderRadius: 8,
                  background: "#fff",
                  borderColor: theme,
                  color: theme,
                  fontSize: 12, fontWeight: 800,
                  boxShadow: `0 3px 10px -3px ${theme}55`,
                }}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
